import React from "react";

import _ from "lodash";

import { DragAndDropListHook } from "commons/hooks/useDragAndDropList";

import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";

import BlocksBuilding from "components/subComponents/BlocksBuilding";
import BlockSelect from "components/subComponents/BlockSelect";
import BlocksPreview from "components/subComponents/BlocksPreview";

import useBlocksForm, { BlocksListItem } from "hooks/form/useBlocksForm";

import { BlockEnum } from "types/enums";

export type BlockGroupContextType = {
    blockGroupCompositionId: string;
    blockGroupId: string;
    blockId: BlockEnum;
    focusedBlock: boolean;
    graphicalOrder: number | null;
    openedBlock: boolean;
    setSingleBlockFocus: (value: boolean) => void;
    setDraggable: React.Dispatch<React.SetStateAction<boolean>>;
    removeBlock: (blockGroupCompositionId: string) => void;
    blockWithErrors: boolean;
    setBlockWithErrors: React.Dispatch<React.SetStateAction<boolean>>;
};

export const BlockGroupContext = React.createContext<BlockGroupContextType>({
    blockGroupCompositionId: "",
    blockGroupId: "",
    blockId: BlockEnum.SectionOne,
    focusedBlock: false,
    graphicalOrder: null,
    openedBlock: false,
    setSingleBlockFocus: (value: boolean) => {},
    setDraggable: (value: React.SetStateAction<boolean>) => {},
    removeBlock: (blockGroupCompositionId: string) => {},
    blockWithErrors: false,
    setBlockWithErrors: (value: React.SetStateAction<boolean>) => {},
});

export const useBlockGroupContext = () => React.useContext(BlockGroupContext);

function BlockItem({
    blockGroupComposition,
    index,
    setBlockGroupComposition,
    removeBlock,
    dragAndDropHook,
    blockGroupId,
    draggable,
    setDraggable,
}: {
    blockGroupComposition: BlocksListItem;
    index: number;
    setBlockGroupComposition: React.Dispatch<React.SetStateAction<BlocksListItem[]>>;
    removeBlock: (blockGroupCompositionId: string) => void;
    dragAndDropHook: DragAndDropListHook<unknown>;
    blockGroupId: string;
    draggable: boolean;
    setDraggable: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const divProps: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    > = React.useMemo(() => {
        const tmpDivProps: React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        > = { className: "" };

        if (dragAndDropHook !== undefined) {
            tmpDivProps.className += " " + dragAndDropHook.classes[index]?.join(" ");

            tmpDivProps.onDragStart = () => dragAndDropHook.onDragStart(index);
            tmpDivProps.onDragEnd = () => dragAndDropHook.onDragEnd(index);
            tmpDivProps.onDragOver = (event) => dragAndDropHook.onDragOver(event);
            tmpDivProps.onDragEnter = () => dragAndDropHook.onDragEnter(index);
            tmpDivProps.onDragLeave = () => dragAndDropHook.onDragLeave(index);
            tmpDivProps.onDrop = () => {
                dragAndDropHook.onDrop(index);

                setDraggable(false);
            };
        }

        return tmpDivProps;
    }, [dragAndDropHook, index, setDraggable]);

    const setSingleBlockFocus = React.useCallback(
        (value: boolean) => {
            setBlockGroupComposition((prev) => {
                const finalValue: BlocksListItem[] = _.cloneDeep(prev);

                finalValue[index].focusedBlock = value;

                return finalValue;
            });
        },
        [setBlockGroupComposition, index]
    );

    const [blockWithErrors, setBlockWithErrors] = React.useState<boolean>(false);

    return blockGroupComposition.deleted ? null : (
        <BlockGroupContext.Provider
            value={{
                blockGroupCompositionId: blockGroupComposition.item.id,
                blockGroupId: blockGroupId,
                blockId: blockGroupComposition.item.block_id,
                graphicalOrder: blockGroupComposition.item.graphical_order,
                openedBlock: blockGroupComposition.openedBlock,
                focusedBlock: blockGroupComposition.focusedBlock,
                setSingleBlockFocus,
                setDraggable,
                removeBlock,
                blockWithErrors,
                setBlockWithErrors,
            }}
            key={blockGroupComposition.item.id}
        >
            <BlocksBuilding
                setSingleBlockFocus={setSingleBlockFocus}
                divProps={divProps}
                draggable={draggable}
            />
        </BlockGroupContext.Provider>
    );
}

function Blocks({ blockGroupId }: { blockGroupId: string }) {
    const blocksFormHook = useBlocksForm({ blockGroupId });

    const { setBlockGroupComposition, removeBlock, dragAndDropHook } = blocksFormHook;

    const [draggable, setDraggable] = React.useState<boolean>(false);

    const fullWidthSelect: boolean = React.useMemo(() => {
        return (
            blocksFormHook.blockGroupComposition.length === 0 ||
            blocksFormHook.blockGroupComposition.every((block) => block.deleted === true)
        );
    }, [blocksFormHook.blockGroupComposition]);

    return (
        <GridItem>
            <GridRow className={"modules"}>
                <GridItem className={"col-xs-12-2"}>
                    <BlocksPreview
                        setDraggable={setDraggable}
                        blockGroupComposition={blocksFormHook.blockGroupComposition}
                        dragAndDropHook={dragAndDropHook}
                        draggable={draggable}
                    />
                </GridItem>

                <GridItem
                    className={
                        (fullWidthSelect ? "col-xs-12-12" : " col-xs-12-10") + " module-list"
                    }
                >
                    <GridColumn>
                        {blocksFormHook.blockGroupComposition.map(
                            (blockGroupComposition, index) => (
                                <BlockItem
                                    key={blockGroupComposition.item.id}
                                    blockGroupComposition={blockGroupComposition}
                                    index={index}
                                    setBlockGroupComposition={setBlockGroupComposition}
                                    removeBlock={removeBlock}
                                    dragAndDropHook={dragAndDropHook}
                                    blockGroupId={blockGroupId}
                                    draggable={draggable}
                                    setDraggable={setDraggable}
                                />
                            )
                        )}

                        <BlockSelect blocksFormHook={blocksFormHook} />
                    </GridColumn>
                </GridItem>
            </GridRow>
        </GridItem>
    );
}

export default Blocks;
